import React, { useEffect, useState } from "react";
import { createNewPlan, updatePlan } from "../../api";
import { validatePlanForm } from "../../utils/validation";
import CreatePlanForm from "./CreatePlanForm";
import { CookieUtils } from "../../axios.config";
const initialFormData = {
  code: "",
  name: "",
  subscriptionAmt: "",
  acctOpenFeesAmt: "",
  tradeCommissionAmt: "",
  orderQtyLimit: "",
  shareCommissionAmt: "",
  aumCommissionPct: "",
  minCommissionAmt: "",
  currencyCd: "INR",
  planSeq: "",
  subscriptionFlg: false,
  planOverrideFlg: true,
  defaultPlanFlg: false,
  feeDesc: "",
  freeTrades: "",
  freeTradesQty: "",
  freeTradesFreqCd: "",
  strikePriceFlg: false,
  stockScope: "",
  hotlineSupportPhone: "",
  hotlineSupportText: "",
  planTitleAdjacent: "",
  planTitleAmount: "",
  createdBy: "",
  updatedBy: "",
  comment: "",
  status: "",
};

const CreatePlan = ({
  mode = "create",
  initialData,
  newPlanAdded,
  userName,
}) => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    const currentUsername = userName || CookieUtils.get("username");
    if (initialData) {
      // Use initialData for both update and create (duplication) modes
      setFormData({
        ...initialFormData,
        ...initialData,
        planOverrideFlg: true,
        createdBy:
          mode === "update"
            ? initialData.createdBy || currentUsername
            : currentUsername,
        updatedBy: mode === "update" ? currentUsername : "",
      });
    } else {
      // No initialData - fresh create
      setFormData({
        ...initialFormData,
        createdBy: currentUsername,
        planOverrideFlg: true,
      });
    }
  }, [mode, initialData, userName]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let val = value;

    if (name === "code") val = val.replace(/[^a-zA-Z0-9_]/g, "").toUpperCase();
    if (type === "checkbox") val = checked;
    if (name === "planOverrideFlg") val = true;
    if (["subscriptionAmt", "acctOpenFeesAmt"].includes(name)) {
      if (!/^\d{0,8}(\.\d{0,2})?$/.test(val)) return;
    }
    if (
      [
        "tradeCommissionAmt",
        "shareCommissionAmt",
        "aumCommissionPct",
        "minCommissionAmt",
      ].includes(name)
    ) {
      if (!/^\d{0,7}(\.\d{0,3})?$/.test(val)) return;
    }
    if (
      ["planSeq", "orderQtyLimit", "freeTrades", "freeTradesQty"].includes(name)
    ) {
      if (!/^\d*$/.test(val)) return;
    }

    setFormData((prev) => ({ ...prev, [name]: val }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDropdownChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateCurrentStep = () => {
    const newErrors = validatePlanForm(formData, currentStep, mode);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateCurrentStep()) setCurrentStep(2);
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
    setErrors({});
  };

  const resetForm = () => {
    setFormData({
      ...initialFormData,
      createdBy: formData.createdBy,
      planOverrideFlg: true,
    });
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;
    setLoading(true);

    const apiData = {
      ...formData,
      planOverrideFlg: true,
      freeTrades: formData.freeTrades
        ? parseInt(formData.freeTrades, 10)
        : formData.freeTrades,
      orderQtyLimit: formData.orderQtyLimit
        ? parseInt(formData.orderQtyLimit, 10)
        : formData.orderQtyLimit,
      freeTradesQty: formData.freeTradesQty
        ? parseInt(formData.freeTradesQty, 10)
        : formData.freeTradesQty,
      planSeq: formData.planSeq
        ? parseInt(formData.planSeq, 10)
        : formData.planSeq,
      ...(mode === "create"
        ? { createdBy: formData.createdBy }
        : { updatedBy: userName || formData.updatedBy || "system" }),
    };
    if (mode === "update") {
      apiData.comment = formData.comment;
    }

    try {
      const response =
        mode === "create"
          ? await createNewPlan(apiData)
          : await updatePlan(formData.planCode || formData.code, apiData);

      if (response?.status === 200 || response?.code === 200) {
        const message =
          mode === "create"
            ? "Plan created successfully!"
            : "Plan updated successfully!";
        setSuccessMessage(message);
        setTimeout(() => {
          setSuccessMessage("");
          resetForm();
          newPlanAdded();
        }, 3000);
      } else if (response?.status === 400) {
        setErrors({
          form:
            response.data?.message ||
            "Plan already exists with the given code.",
        });
      } else {
        setErrors({ form: response?.data?.message || "Operation failed!" });
      }
    } catch (error) {
      setErrors({
        form: error.message || "An error occurred. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <CreatePlanForm
      mode={mode}
      formData={formData}
      errors={errors}
      currentStep={currentStep}
      successMessage={successMessage}
      loading={loading}
      onChange={handleChange}
      onDropdownChange={handleDropdownChange}
      onNextStep={handleNextStep}
      onPreviousStep={handlePreviousStep}
      onSubmit={handleSubmit}
    />
  );
};

export default CreatePlan;
